import { useState, useMemo } from "react";
import { Search } from "lucide-react";
import { motion } from "framer-motion";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Reveal, RevealGroup } from "@/components/Reveal";
import ArticleCard from "@/components/ArticleCard";
import { GUIDES, GUIDE_CATEGORIES } from "@/data/guides";

export default function Guides() {
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState("All");

  const featured = GUIDES.find((g) => g.featured);
  const filtered = useMemo(() => {
    return GUIDES.filter((g) => {
      const matchCat = cat === "All" || g.category === cat;
      const q = query.trim().toLowerCase();
      const matchQ = !q || g.title.toLowerCase().includes(q) || g.excerpt.toLowerCase().includes(q);
      return matchCat && matchQ;
    });
  }, [query, cat]);

  return (
    <div data-testid="guides-page">
      <Seo
        title="Homeowner Guides"
        description="Practical, homeowner-first pest-control guides — mosquito pricing, DIY that works, tick treatments, and what to check before hiring a pro."
        path="/guides"
      />
      <section className="bg-forest-950 text-cream grain pt-[72px]">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-14 md:py-20">
          <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Guides" }]} />
          <h1 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.02] max-w-3xl">
            The homeowner guides
          </h1>
          <p className="mt-5 text-lg text-cream/80 max-w-2xl leading-relaxed">
            Straight answers on pricing, DIY, and hiring — written for homeowners, not for search engines.
          </p>
          <div className="mt-8 relative max-w-md">
            <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-cream/50" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search guides…"
              className="w-full pl-12 pr-5 py-3.5 rounded-full bg-cream/10 border border-cream/20 text-cream placeholder:text-cream/40 focus:border-duckgreen outline-none transition-colors"
              data-testid="guides-search-input"
            />
          </div>
        </div>
      </section>

      {/* Featured */}
      {featured && cat === "All" && !query && (
        <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pt-14">
          <Reveal>
            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-terracotta">Featured</span>
            <div className="mt-4">
              <RevealGroup className="grid lg:grid-cols-2 gap-5">
                <ArticleCard guide={featured} featured />
                <div className="grid gap-5">
                  {GUIDES.filter((g) => g.featured && g.slug !== featured.slug).slice(0, 2).map((g) => (
                    <ArticleCard key={g.slug} guide={g} />
                  ))}
                </div>
              </RevealGroup>
            </div>
          </Reveal>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-14 md:py-16">
        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-10">
          {GUIDE_CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                cat === c ? "bg-forest-950 text-cream" : "bg-pill/70 text-forest-950/70 hover:bg-pill"
              }`}
              data-testid={`guides-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              {c}
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-20" data-testid="guides-empty">
            <p className="font-serif text-2xl text-forest-950">No guides match that yet.</p>
            <p className="mt-2 text-forest-950/60">Try a different search or category.</p>
          </motion.div>
        ) : (
          <RevealGroup className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((g) => (
              <ArticleCard key={g.slug} guide={g} />
            ))}
          </RevealGroup>
        )}
      </section>
    </div>
  );
}
